import React, { Dispatch, SetStateAction } from 'react'
import { useSelector } from 'react-redux'

import { Box, makeStyles } from '@material-ui/core'

import { B2CCard } from '@masfront/common-b2c/src/modules/ui'
import { CustomTheme } from '@masfront/common-b2c/src/modules/ui/theme//utils'
import { greyMultibrandPalette } from '@masfront/common-b2c/src/modules/ui/theme/colors'

import { selectAgent } from '~/auth/store/auth.selectors'
import { useBrand } from '~/brand'
import { useAppDepsContext } from '~/core/hooks/useAppDepsContext'
import {
  getPackageFromProductId,
  getAccountFromProductId,
  getTariffDescription,
  subscriptionTypeText,
} from '~/customer/helpers'
import {
  Customer,
  Subscription,
  SubscriptionType,
  SubscriptionStatus,
} from '~/customer/model/customer.model'
import SubscriptionCardFooter from '~/offersConfigurator/containers/SubscriptionCardFooter'
import useDownloadContract from '~/offersConfigurator/hooks/useDownloadContract'
import { SALES_PATHS } from '~/pages/router/paths'
import Permission from '~/permissions/model/permissions.model'
import { selectHasPermission } from '~/permissions/store/permissions.selectors'
import useSubscriptionDetail from '~/subscriptionDetail/hooks/useSubscriptionDetail'
import { B2CModal, CustomCircularProgress } from '~/ui/components'
import ProductStatusDetail from '~/ui/components/ProductStatusDetail/ProductStatusDetail'

const useStyles = makeStyles((theme: CustomTheme) => ({
  titleList: {
    display: 'flex',
    padding: 0,
    margin: 0,
    lineHeight: '0.9',
    '& li': {
      display: 'flex',
      flexDirection: 'row',
      padding: theme.spacing(0, 3),
      borderLeft: `2px ${greyMultibrandPalette[900]} solid`,
    },
    '& li:first-child': {
      paddingLeft: 0,
      borderLeft: 'none',
    },
  },
  downloadContractLoading: {
    position: 'absolute',
    right: '50%',
    zIndex: 100,
    transform: 'translateX(50%)',
    marginTop: '15px',
  },
}))

interface OffersConfiguratorCardProps {
  customer: Customer
  subscription: Subscription
  setSubscriptionIdInModal: Dispatch<SetStateAction<string>>
  setIsOpenBonusesAvailableModal: Dispatch<SetStateAction<boolean>>
}

interface Action {
  label?: string
  onClick: () => void
  disabled?: boolean
  isLoading?: boolean
}

const OffersConfiguratorCard: React.FC<OffersConfiguratorCardProps> = ({
  customer,
  subscription,
  setSubscriptionIdInModal,
  setIsOpenBonusesAvailableModal,
}) => {
  const styles = useStyles()
  const { getSubscriptionDetail, subscriptionDetail } = useSubscriptionDetail(subscription.id)
  const subscriptionDetailLoading = useSubscriptionDetail(subscription.id).loading
  const { brand } = useBrand()
  const { agent } = useSelector(selectAgent)
  const {
    downloadContract,
    downloadContractState,
    cleanDownloadContractError,
  } = useDownloadContract()
  const hasPermissionsSellPortfolio = useSelector(selectHasPermission(Permission.SELL_PORTFOLIO))
  const hasPermissionAdministrateBonuses = useSelector(
    selectHasPermission(Permission.ADMINISTRATE_BONUSES),
  )

  const contractLoading = downloadContractState.loading
  const contractError = downloadContractState.error
  const provinceId = String(customer && customer.addressInfo.stateId)

  const { runtimeConfig } = useAppDepsContext()
  const document = customer.documentValue
  const { documentType } = customer
  const newContractURL = `${runtimeConfig.salesHost}${SALES_PATHS.SALES_START}?brand=${brand}&document=${document}&documentType=${documentType}`

  const handleCardClick = () => {
    if (!subscriptionDetail) {
      getSubscriptionDetail({
        subscriptionId: subscription.id,
        provinceId,
        brand,
        sub: agent?.sub,
      })
    }
  }

  const isBonusesDisabled = (subscription: Subscription) => {
    return (
      subscription.type !== SubscriptionType.PREPAID ||
      subscription.status !== SubscriptionStatus.ACTIVATED
    )
  }

  const openBonusModal = (subscription: Subscription) => {
    const { id } = subscription

    setSubscriptionIdInModal(`${id}`)
    setIsOpenBonusesAvailableModal(true)
  }

  const pkg = customer && getPackageFromProductId(customer, subscription.identificationNr)
  const footerInfo = subscription && [
    subscription.identificationNr,
    subscription.type !== SubscriptionType.DIGITAL_FIXED &&
    subscription.type !== SubscriptionType.ANALOG_FIXED
      ? subscriptionTypeText[subscription.type].text
      : '',
    subscription.type === SubscriptionType.ADSL || subscription.type === SubscriptionType.FFTH
      ? 'Internet'
      : '',
  ]
  const preHeader = (
    <ProductStatusDetail status={subscription.status} statusGroup={subscription.statusGroup} />
  )

  let menuActions: Action[] = [
    {
      label: !subscriptionDetailLoading ? 'Descargar contrato' : '',
      isLoading: !!subscriptionDetailLoading,
      onClick: () => downloadContract(subscription.id),
      disabled: !subscriptionDetail?.hasContract,
    },
  ]

  if (hasPermissionsSellPortfolio) {
    menuActions = [
      ...menuActions,
      {
        label: 'Añadir línea móvil',
        onClick: () =>
          window.open(
            `${newContractURL}&accountNumber=${
              customer
                ? getAccountFromProductId(customer, subscription.identificationNr)?.accountNumber
                : ''
            }`,
            '_self',
          ),
        disabled: subscription.status !== SubscriptionStatus.ACTIVATED,
      },
    ]
  }

  if (hasPermissionAdministrateBonuses) {
    menuActions = [
      ...menuActions,
      {
        label: 'Añadir bono',
        onClick: () => customer && openBonusModal(subscription),
        disabled: isBonusesDisabled(subscription),
      },
    ]
  }

  return (
    <Box width="100%" gridColumn="1 / span 3" gridRow="auto / auto" onClick={handleCardClick}>
      {contractLoading && (
        <Box position="relative">
          <Box className={styles.downloadContractLoading}>
            <CustomCircularProgress size={80} />
          </Box>
        </Box>
      )}
      <B2CCard
        title={
          <ul className={styles.titleList}>
            <li>{pkg && getTariffDescription(pkg)}</li>
            <li>{subscription.identificationNr}</li>
          </ul>
        }
        preHeader={preHeader}
        footer={<SubscriptionCardFooter footerItems={footerInfo} />}
        menuActions={menuActions}
        menuActionsButtonText="Opciones"
        cardId={subscription.identificationNr}
      />
      <B2CModal
        isOpen={!!contractError}
        type="error"
        title="No se ha podido descargar el contrato"
        onClose={cleanDownloadContractError}
      >
        <Box m={15} />
      </B2CModal>
    </Box>
  )
}

export default OffersConfiguratorCard
